import { LocalLogger, LogLevel } from '@aninix/logger'
import { ArrayBufferTarget, Muxer } from 'mp4-muxer'
import * as R from 'ramda'
import { yieldToMain } from '../../utils'
import { Encoder } from './types'

const logger = new LocalLogger({
  level: process.env.LOG_LEVEL as LogLevel,
  prefix: '[encodeMp4]',
})

export const encodeMp4: Encoder = ({
  iterable,
  options,
  onProgressUpdate,
  onError,
  onDone,
}) => {
  const { fps, framesCount, size } = options
  let isCancelled = false
  let videoEncoder: VideoEncoder

  const render = async () => {
    try {
      const muxer = new Muxer({
        fastStart: false,
        target: new ArrayBufferTarget(),
        video: {
          codec: 'avc',
          width: size.x,
          height: size.y,
        },
        firstTimestampBehavior: 'offset',
      })
      let prevTime = Date.now()
      let processedFrame = 0
      videoEncoder = new VideoEncoder({
        output: (chunk, meta) => {
          processedFrame += 1
          const end = Date.now()
          onProgressUpdate(
            R.clamp(0, 1, processedFrame / framesCount),
            end - prevTime
          )
          logger.log(
            `combining frame ${processedFrame}/${framesCount} time in ms`,
            end - prevTime
          )
          prevTime = Date.now()
          return muxer.addVideoChunk(chunk, meta!)
        },
        error: (e) => {
          onError(e.message)
        },
      })
      const config: VideoEncoderConfig = {
        // @NOTE: enable once we have correct levels available.
        // https://en.wikipedia.org/wiki/Advanced_Video_Coding#Levels
        // codec: 'avc1.6E103D',
        codec: 'avc1.640029',
        // @NOTE: https://developer.mozilla.org/en-US/docs/Web/Media/Formats/codecs_parameter#av1
        // codec: 'av01.0.15M.08',
        bitrateMode: 'variable',
        framerate: fps,
        width: size.x,
        height: size.y,
        bitrate: 50e6,
      }
      console.log(await VideoEncoder.isConfigSupported(config))
      videoEncoder.configure(config)

      let i = 0
      for await (const canvas of iterable) {
        if (isCancelled) {
          return
        }
        if (i % 20 === 0) {
          await yieldToMain()
        }
        const videoFrame = new VideoFrame(canvas, {
          timestamp: i * (1 / options.fps) * 1e6,
        })
        videoEncoder.encode(videoFrame, {
          keyFrame: i % 10 === 0,
        })
        videoFrame.close()
        i++
      }

      await videoEncoder.flush()

      muxer.finalize()
      onDone(new Blob([muxer.target.buffer], { type: 'video/mp4' }))
    } catch (err: any) {
      onError(
        `Unexpected issue happen. Please send the following info to us: ${err.message}`
      )
      logger.error(err)
    }
  }

  render()

  return () => {
    try {
      isCancelled = true
      videoEncoder.close()
    } catch (err: any) {
      logger.warn('cancelation error', err)
    }
  }
}
